import * as THREE from 'three';
import Camera from './Components/Camera.js';
import Renderer from './Systems/Renderer.js';
import Resizer from './Utils/Resizer.js';
import Time from './Utils/Time.js';
import Space from './Components/Space.js';
import Card from './Components/Card.js';
import DeckLoader from './Utils/DeckLoader.js';

import EventBus from './Systems/EventBus.js';
import Interaction from './Systems/Interaction.js';
import RemoteCursors from './Components/RemoteCursors.js';
import defaultDeck from './Configs/deck-nature.js'
import VignetteFX from './Components/VignetteFX.js';
import { theme } from './Utils/theme.js';
import { createBehavior } from './Behaviors/index.js';

let instance = null;

export default class Experience {
    constructor(canvas, state) {
        // singleton
        if (instance) {
            return instance;
        }
        instance = this;
        window.experience = this;
        this.canvas = canvas;
        this.channel = state.channel;
        this.presence = state.presence;
        this.user_id = state.user_id;
        this.instance_id = state.instance_id;
        this.settings = state.experience_settings || {};
        this.runtimeState = state.experience_state || this.settings.experience_state || {};

        const deck = state.deck || defaultDeck;
        const zonesLayout = state.zones_layout || null;
        const deckWithPositions = this.applyCardPositions(deck, state.card_positions);

        // setup (order matters)
        this.eventBus = new EventBus(this.user_id, state.channel);
        theme.init(this.eventBus, this.settings.theme_name);
        this.resizer = new Resizer(this.canvas.parentNode);
        this.time = new Time();
        this.scene = new THREE.Scene();
        this.deckLoader = new DeckLoader(deckWithPositions);
        this.camera = new Camera(this.settings);
        this.renderer = new Renderer(this.settings);
        this.zonesLayout = zonesLayout;
        this.space = new Space(zonesLayout);

        // Create behavior and wire it up
        const behaviorName = this.settings.behavior || 'SandboxBehavior';
        this.behavior = createBehavior(behaviorName, this.space, null, this.runtimeState);
        this.space.installRemoteHandlers(this.behavior.getRemoteHandlers());

        this.interaction = new Interaction();
        // Now that Interaction exists, give the behavior a reference to it
        this.behavior.interaction = this.interaction;
        this.applyBehaviorState(this.runtimeState);
        this.remoteCursors = new RemoteCursors();

        this.resize();
        this.eventBus.on('resize', (data) => this.resize(data));
        this.time.registerUpdate(this.update.bind(this));
    }

    applyCardPositions(deck, cardPositions) {
        if (!cardPositions || Object.keys(cardPositions).length === 0) {
            return deck;
        }
        const merged = JSON.parse(JSON.stringify(deck));
        for (const card of merged.cards) {
            const pos = cardPositions[card.id];
            if (pos) {
                if (pos.x !== undefined) card.x = pos.x;
                if (pos.y !== undefined) card.y = pos.y;
                if (pos.z !== undefined) card.z = pos.z;
                if (pos.index !== undefined) card.index = pos.index;
                if (pos.face_up !== undefined) card.face_up = pos.face_up;
            }
        }
        return merged;
    }

    applySettings(settings) {
        const hasValue = (value) => value !== undefined && value !== null;
        const previousSettings = this.settings || {};
        this.settings = settings || {};

        if (this.settings.experience_state !== undefined) {
            this.runtimeState = this.settings.experience_state || {};
        }

        // Behavior changed → full reset to load new behavior
        if (hasValue(this.settings.behavior) && this.settings.behavior !== previousSettings.behavior) {
            const deck = this.deckLoader?.deck;
            if (deck) {
                this.reset(deck);
                return;
            }
        }

        // Theme
        if (hasValue(this.settings.theme_name) && this.settings.theme_name !== previousSettings.theme_name) {
            theme.assignToPageAndThree(this.settings.theme_name);
        }

        // Camera
        if (this.camera) {
            this.camera.applySettings(this.settings);
        }

        // Renderer
        if (this.renderer) {
            this.renderer.applySettings(this.settings);
        }

        // Light (lives inside Space)
        if (this.space && this.space.light) {
            this.space.light.applySettings(this.settings);
        }

        const textureSamplingChanged =
            this.settings.texture_min_filter !== previousSettings.texture_min_filter ||
            this.settings.texture_mag_filter !== previousSettings.texture_mag_filter;
        if (this.space && textureSamplingChanged) {
            this.space.applyTextureSampling();
        }

        if (this.space) {
            this.space.applyCardSettings(this.settings, previousSettings);
        }

        if (hasValue(this.settings.clear_color) && this.settings.clear_color !== previousSettings.clear_color) {
            this.applyVignetteColor(this.settings.clear_color);
        }

        // Vignette
        if (this.vignetteMesh && hasValue(this.settings.vignette_enabled)) {
            this.vignetteMesh.visible = !!this.settings.vignette_enabled;
        }
        if (this.vignetteModifiers) {
            const hasFocusedCard = !!this.space?.focusedCard;

            if (this.settings.vignette_sharpness !== undefined) {
                this.vignetteModifiers.sharpnessInitial = this.settings.vignette_sharpness;
                if (!hasFocusedCard) {
                    this.vignetteModifiers.sharpness = this.settings.vignette_sharpness;
                }
            }
            if (this.settings.vignette_opaqueness !== undefined) {
                this.vignetteModifiers.opaquenessInitial = this.settings.vignette_opaqueness;
                this.vignetteModifiers.opaqueness = this.settings.vignette_opaqueness;
            }
            if (this.settings.vignette_swell !== undefined) {
                this.vignetteModifiers.circleSwellInitial = this.settings.vignette_swell;
                this.vignetteModifiers.circleSwell = this.settings.vignette_swell;
            }
            if (this.settings.vignette_base_alpha !== undefined) {
                this.vignetteModifiers.baseAlphaInitial = this.settings.vignette_base_alpha;
                if (!hasFocusedCard) {
                    this.vignetteModifiers.baseAlpha = this.settings.vignette_base_alpha;
                }
            }
        }
    }

    applyBehaviorState(state) {
        this.runtimeState = state || {};
        this.settings = {
            ...(this.settings || {}),
            experience_state: this.runtimeState,
        };

        if (this.behavior && typeof this.behavior.applyState === 'function') {
            this.behavior.applyState(this.runtimeState);
        }
    }

    async loadShaders() {
        await VignetteFX(this, {
            u_sharpness: this.settings.vignette_sharpness,
            u_opaqueness: this.settings.vignette_opaqueness,
            u_circleSwell: this.settings.vignette_swell,
            u_baseAlpha: this.settings.vignette_base_alpha,
        });

        this.applySettings(this.settings || {});
    }

    applyVignetteColor(clearColor) {
        const uniformColor = this.vignetteMesh?.material?.uniforms?.u_color?.value;
        if (!uniformColor || !clearColor) return;

        const vignetteColor = new THREE.Color(clearColor).multiplyScalar(0.6);
        uniformColor.copy(vignetteColor);
    }

    resize(bounds) {
        this.camera.resize(bounds);
        this.renderer.resize(bounds);
        this.space.resize(bounds);

    }

    update(delta) {
        this.camera.update(delta);
        this.space.update(delta);
        this.remoteCursors.update(delta);
        this.renderer.update(delta);
    }

    reset(newDeck, newSettings) {
        if (newSettings) {
            this.settings = newSettings;
        }

        this.scene.traverse((child) => {
            if (child instanceof THREE.Mesh && child.name === 'wrapper') {
                child.geometry.dispose();
                for (const key in child.material) {
                    const value = child.material[key];
                    if (value && typeof value.dispose === 'function') {
                        value.dispose();
                    }
                }
            }
        });

        if (this.behavior) {
            this.behavior.onDestroy();
        }

        if (this.space) {
            this.space.destroy();
        }
        if (this.interaction) {
            this.interaction.destroy();
        }

        // Rebuild with new deck
        this.deckLoader = new DeckLoader(newDeck);
        this.space = new Space(this.zonesLayout);

        const behaviorName = this.settings.behavior || 'SandboxBehavior';
        this.behavior = createBehavior(behaviorName, this.space, null, this.runtimeState);
        this.space.installRemoteHandlers(this.behavior.getRemoteHandlers());

        this.interaction = new Interaction();
        this.behavior.interaction = this.interaction;
        this.applyBehaviorState(this.runtimeState);
        this.remoteCursors = new RemoteCursors();

        // Remove old vignette before loadShaders creates a new one
        if (this.vignetteMesh) {
            this.vignetteMesh.geometry.dispose();
            this.vignetteMesh.material.dispose();
            this.vignetteMesh.removeFromParent();
            this.vignetteMesh = null;
        }

        this.applySettings(this.settings);
        this.applyBehaviorState(this.runtimeState);
        this.loadShaders();
    }

    resetZones(newLayout) {
        this.zonesLayout = newLayout;
        if (this.space) {
            this.space.resetZones(newLayout);
        }
    }

    addCardFromServer(cardData) {
        if (this.space) {
            this.space.addCardDynamic(cardData);
        }
    }

    replaceCardTexture(cardId, textureUrl, side) {
        if (this.space) {
            try {
                const card = this.space.getCardById(cardId);
                card.replaceTexture(textureUrl, side);
            } catch (e) {
                console.warn(`replaceCardTexture: card ${cardId} not found`);
            }
        }
    }

    handleAssetChanged(type, path) {
        if (!this.space) return;

        const cacheBust = `${path}?t=${Date.now()}`;

        if (type === 'texture') {
            const sources = this.deckLoader?.deck?.cards || [];
            const cards = this.deckLoader?.cards || [];

            sources.forEach(source => {
                const card = cards.find(c => c.id === source.id);
                if (!card) return;

                if (source.front?.texture === path) {
                    card.replaceTexture(cacheBust, Card.FRONT_SIDE);
                }
                if (source.back?.texture === path) {
                    card.replaceTexture(cacheBust, Card.BACK_SIDE);
                }
            });
        }
    }

    handleCardsArranged(cards) {
        if (!this.space) return;

        for (const data of cards) {
            try {
                const card = this.space.getCardById(data.id);
                card.setPosition(new THREE.Vector3(data.x, data.y, data.z));
                if (data.face_up && !card.isFaceUp) card.faceUp();
                if (!data.face_up && card.isFaceUp) card.faceDown();
            } catch (e) {
                console.warn(`handleCardsArranged: card ${data.id} not found`);
            }
        }
    }

    /**
     * Applies a server card_positions map onto the cards already in the scene.
     * Returns false when the scene cannot represent the payload, so callers can
     * fall back to a full reset instead of silently drifting.
     */
    syncCardPositions(cardPositions) {
        if (!this.space) return false;
        if (!cardPositions || Object.keys(cardPositions).length === 0) return true;

        for (const [id, position] of Object.entries(cardPositions)) {
            let card;

            try {
                card = this.space.getCardById(id);
            } catch (e) {
                return false;
            }

            const target = card.targetPosition;
            card.setPosition(new THREE.Vector3(
                position.x ?? target.x,
                position.y ?? target.y,
                position.z ?? target.z
            ));

            if (position.face_up !== undefined) {
                if (position.face_up && !card.isFaceUp) card.faceUp();
                if (!position.face_up && card.isFaceUp) card.faceDown();
            }

            if (position.index !== undefined) card.index = position.index;
        }

        this.space.getAllCards()
            .slice()
            .sort((a, b) => a.index - b.index)
            .forEach((card, index) => card.setIndex(index));

        return true;
    }

    destroy() {
        this.resizer.destroy()
        this.time.destroy()

        if (this.behavior) {
            this.behavior.onDestroy();
            this.behavior = null;
        }

        if (this.space) {
            this.space.destroy()
            this.space = null
        }

        if (this.interaction) {
            this.interaction.destroy()
            this.interaction = null
        }

        if (this.remoteCursors) {
            this.remoteCursors.destroy()
            this.remoteCursors = null
        }

        this.scene.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                child.geometry.dispose()

                for (const key in child.material) {
                    const value = child.material[key]
                    if (value && typeof value.dispose === 'function') {
                        value.dispose()
                    }
                }
            }
        })

        if (this.camera.controls) {
            this.camera.controls.dispose()
        }
        this.renderer.instance.dispose()

        instance = null;
    }
}
